import type {
  PublicApprovalView,
  PublicCustomerDisplayMode,
  PublicPricingCategory,
  PublicPricingMetadata,
  PublicPricingUnit,
} from "@/lib/public-approval";

export type ApprovalPricingDisplay =
  | { kind: "final"; total: number; metadata: PublicPricingMetadata | null; showMetadataTotals: boolean }
  | {
      kind: "summary";
      total: number;
      metadata: PublicPricingMetadata;
      entries: Array<{ category: PublicPricingCategory; label: string; total: number }>;
    }
  | {
      kind: "itemized";
      total: number;
      metadata: PublicPricingMetadata;
      rows: Array<{
        id: string;
        description: string;
        category: PublicPricingCategory;
        quantity: number;
        unit: PublicPricingUnit;
        unitCost: number;
        thumbnailPath?: string | null;
        thumbnailUrl?: string | null;
        total: number;
      }>;
    }
  | { kind: "legacy"; total: number };

const CATEGORY_LABELS: Record<PublicPricingCategory, string> = {
  labor: "Labor / Service",
  materials: "Materials",
  equipment: "Equipment",
  subcontractor: "Subcontractor",
  other: "Other",
};

const UNIT_LABELS: Record<PublicPricingUnit, string> = {
  hrs: "hrs",
  ea: "ea",
  box: "box",
  bag: "bag",
  sheet: "sheet",
  bucket: "bucket",
  roll: "roll",
  ft: "ft",
  lf: "lf",
  sq_ft: "sq ft",
  gal: "gal",
  cu_yd: "cu yd",
  lb: "lb",
  day: "day",
  fixed: "fixed",
  other: "other",
};

function positiveNumber(value: unknown) {
  const numberValue = Number(value ?? 0);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : 0;
}

function sumLegacyLineItems(approval: PublicApprovalView) {
  return approval.lineItems.reduce((sum, item) => sum + Number(item.totalCost || 0), 0);
}

function hasLegacyLineItems(approval: PublicApprovalView) {
  return approval.lineItems.some((item) => Number(item.totalCost || 0) !== 0 || item.description.trim());
}

export function formatPricingCategoryLabel(category?: string | null) {
  return CATEGORY_LABELS[(category as PublicPricingCategory) ?? "other"] ?? CATEGORY_LABELS.other;
}

export function formatPricingUnitLabel(unit?: string | null) {
  return UNIT_LABELS[(unit as PublicPricingUnit) ?? "other"] ?? UNIT_LABELS.other;
}

export function getCustomerDisplayMode(approval: PublicApprovalView): PublicCustomerDisplayMode {
  return approval.pricingMetadata?.customerDisplayMode ?? "final_price_only";
}

export function getCustomerFacingTotal(approval: PublicApprovalView) {
  const metadata = approval.pricingMetadata;
  if (metadata) {
    const finalAmount = positiveNumber(metadata.finalAmount);
    if (finalAmount) return finalAmount;

    const calculated = Number(metadata.customerPrice || 0) - Number(metadata.discountAmount || 0) + Number(metadata.taxAmount || 0);
    if (Number.isFinite(calculated) && calculated > 0) return calculated;

    const customerFacingFinalPrice = positiveNumber(metadata.internalEstimate?.customerFacingFinalPrice);
    if (customerFacingFinalPrice) return customerFacingFinalPrice;
  }

  const ticketTotal = positiveNumber(approval.total);
  if (ticketTotal) return ticketTotal;

  return sumLegacyLineItems(approval);
}

export function getSummaryBreakdown(approval: PublicApprovalView) {
  const rows = approval.pricingMetadata?.internalEstimate?.rows ?? [];
  const totals = new Map<PublicPricingCategory, number>();

  for (const row of rows) {
    const total = Number(row.lineTotal || 0);
    if (!Number.isFinite(total) || total <= 0) continue;
    const category = row.category ?? "other";
    totals.set(category, (totals.get(category) ?? 0) + total);
  }

  return Array.from(totals.entries())
    .map(([category, total]) => ({
      category,
      label: formatPricingCategoryLabel(category),
      total,
    }))
    .filter((entry) => entry.total > 0);
}

export function getCustomerVisibleLineItems(approval: PublicApprovalView) {
  const rows = approval.pricingMetadata?.internalEstimate?.rows ?? [];
  const visibleRows = rows.filter((row) => row.visibleToCustomer);
  const candidateRows = visibleRows.length > 0 ? visibleRows : rows;

  return candidateRows
    .filter((row) => row.description.trim() && Number(row.lineTotal || 0) > 0)
    .map((row) => ({
      id: row.id,
      description: row.description,
      category: row.category,
      quantity: Number(row.quantity || 0),
      unit: row.unit,
      unitCost: Number(row.unitCost || 0),
      thumbnailPath: row.thumbnailPath ?? null,
      thumbnailUrl: row.thumbnailUrl ?? null,
      total: Number(row.lineTotal || 0),
    }));
}

export function getApprovalPricingDisplay(approval: PublicApprovalView): ApprovalPricingDisplay {
  const metadata = approval.pricingMetadata ?? null;
  const total = getCustomerFacingTotal(approval);

  if (!metadata) {
    return hasLegacyLineItems(approval) ? { kind: "legacy", total } : { kind: "final", total, metadata: null, showMetadataTotals: false };
  }

  const displayMode = getCustomerDisplayMode(approval);

  if (displayMode === "summary_breakdown") {
    const entries = getSummaryBreakdown(approval);
    if (entries.length > 0) return { kind: "summary", total, metadata, entries };
    return { kind: "final", total, metadata, showMetadataTotals: true };
  }

  if (displayMode === "full_itemized_breakdown") {
    const rows = getCustomerVisibleLineItems(approval);
    if (rows.length > 0) return { kind: "itemized", total, metadata, rows };

    return { kind: "final", total, metadata, showMetadataTotals: true };
  }

  return { kind: "final", total, metadata, showMetadataTotals: true };
}
